import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import axios from "../../api";
import { useSelector } from "react-redux";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { RootState } from "store";
const localizer = momentLocalizer(moment);

function parseDateOnlyLocalFromUnknown(
  dateVal: string | number | Date | null | undefined
): Date | null {
  if (dateVal == null || dateVal === "") return null;
  if (typeof dateVal === "string") {
    const match = dateVal.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );
  }
  const dateObj = new Date(dateVal);
  if (Number.isNaN(dateObj.getTime())) return null;
  return new Date(
    dateObj.getUTCFullYear(),
    dateObj.getUTCMonth(),
    dateObj.getUTCDate()
  );
}

function buildEmployeeArrivalEventTimes(
  dateVal: string | number | Date | null | undefined,
  timeVal: string | null | undefined
): { start: Date; end: Date; allDay: boolean } | null {
  const localDate = parseDateOnlyLocalFromUnknown(dateVal);
  if (!localDate) return null;
  const hasTime = timeVal != null && String(timeVal).trim() !== "";
  if (hasTime) {
    const t = String(timeVal).trim();
    const tm = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!tm) return null;
    const hour = Number(tm[1]);
    const minute = Number(tm[2]);
    const second = tm[3] ? Number(tm[3]) : 0;
    if (hour > 23 || minute > 59 || second > 59) return null;
    const start = new Date(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      hour,
      minute,
      second
    );
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    return { start, end, allDay: false };
  }
  const start = new Date(
    localDate.getFullYear(),
    localDate.getMonth(),
    localDate.getDate(),
    0,
    0,
    0,
    0
  );
  const end = new Date(
    localDate.getFullYear(),
    localDate.getMonth(),
    localDate.getDate(),
    23,
    59,
    59,
    999
  );
  return { start, end, allDay: true };
}
interface Event {
  ticketId: number;
  ticketid: number;
  latitude: number;
  longitude: number;
  Customer: string;
  Phone: string;
  State: string;
  City: string;
  Region: string;
  address: string;
  description: string;
  category: string;
  allDay?: boolean;
}

interface TicketDetailItem {
  label: string;
  value: string | number;
}

interface CalendarEvent extends Event {
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
}

const EventsCalendar = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showMoreEvents, setShowMoreEvents] = useState<CalendarEvent[]>([]);
  const [showMoreDate, setShowMoreDate] = useState<Date | null>(null);
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTodoTickets = async () => {
      try {
        const response = await axios.get(`/tickets/employee/${user?.userId}`);
        const tickets = Array.isArray(response.data)
          ? response.data
          : response.data.list || [];
        const eventsData = tickets
          .map(
            (ticket: {
              ticket_service_id: string;
              employee_arrival_date: string | number | Date;
              employee_arrival_time?: string | null;
              ticket_id: number;
              customer_name: string;
              customer_phone: string;
              state_name: string;
              city_name: string;
              region_name: string;
              address: string;
              description: string;
              category_name: string;
              latitude: number;
              longitude: number;
            }) => {
              const slot = buildEmployeeArrivalEventTimes(
                ticket.employee_arrival_date,
                ticket.employee_arrival_time ?? null
              );
              if (!slot) return null;
              return {
                title: `Ticket ${ticket.ticket_service_id}`,
                start: slot.start,
                end: slot.end,
                allDay: slot.allDay,
                ticketId: ticket.ticket_service_id,
                ticketid: ticket.ticket_id,
                Customer: ticket.customer_name,
                Phone: ticket.customer_phone,
                State: ticket.state_name,
                City: ticket.city_name,
                Region: ticket.region_name,
                address: ticket.address,
                description: ticket.description,
                category: ticket.category_name,
                latitude: ticket.latitude,
                longitude: ticket.longitude,
              };
            }
          )
          .filter(Boolean) as CalendarEvent[];

        setEvents(eventsData);
      } catch (error) {
        console.error("Error fetching tickets:", error);
      }
    };

    fetchTodoTickets();
  }, [user?.userId]);

  const handleEventClick = (event: React.SetStateAction<Event | null>) => {
    setSelectedEvent(event);
  };

  const closeModal = () => {
    setSelectedEvent(null);
  };

  const eventStyleGetter = (event: { start: moment.MomentInput }) => {
    const eventDate = moment(event.start);
    const currentDate = moment();
    let backgroundColor = eventDate.isSameOrAfter(currentDate, "day")
      ? "#00bdaa"
      : "#FE346E";

    return {
      style: {
        backgroundColor,
        color: "white",
        borderRadius: "4px",
        padding: "4px",
        border: "none",
      },
    };
  };

  const handleViewTicket = () => {
    if (selectedEvent) {
      navigate(`/employee/ticket-view/${selectedEvent.ticketid}`, {
        state: { from: "/employee/calender" },
      });
    }
  };

  const handleShowMore = (moreEvents: CalendarEvent[], date: Date) => {
    setShowMoreEvents(moreEvents);
    setShowMoreDate(date);
  };

  const closeShowMoreModal = () => {
    setShowMoreEvents([]);
    setShowMoreDate(null);
  };

  const handleShowMoreEventClick = (event: CalendarEvent) => {
    closeShowMoreModal();
    setSelectedEvent(event);
  };
  // Create ticket details array with proper keys
  const getTicketDetails = (event: Event): TicketDetailItem[] => [
    { label: "Ticket ID", value: event.ticketId },
    { label: "Customer", value: event.Customer },
    { label: "Phone", value: event.Phone },
    { label: "State", value: event.State },
    { label: "City", value: event.City },
    { label: "Region", value: event.Region },
    { label: "Address", value: event.address },
    { label: "Description", value: event.description },
    { label: "Category", value: event.category },
  ];

  // Check if location data is available (lat/lng or address for Google Maps)
  const hasLocationData =
    Boolean(selectedEvent?.latitude) && Boolean(selectedEvent?.longitude);
  const hasAddress =
    Boolean(selectedEvent?.address) ||
    Boolean(selectedEvent?.City) ||
    Boolean(selectedEvent?.State);
  const mapQuery = hasLocationData
    ? `${selectedEvent?.latitude},${selectedEvent?.longitude}`
    : hasAddress
      ? [selectedEvent?.address, selectedEvent?.City, selectedEvent?.State, selectedEvent?.Region]
          .filter(Boolean)
          .join(", ")
      : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "1200px",
            padding: "40px",
          }}
        >
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 700, width: "100%" }}
            selectable
            views={["month", "week", "day", "agenda"]}
            defaultView="month"
            step={30}
            timeslots={2}
            dayLayoutAlgorithm="no-overlap"
            popup={false}
            doShowMoreDrillDown={false}
            onShowMore={handleShowMore}
            onSelectEvent={handleEventClick}
            className="custom-calendar"
            eventPropGetter={eventStyleGetter}
            messages={{
              showMore: (total) => `+${total} more`,
            }}
          />

          <Modal open={showMoreEvents.length > 0} onClose={closeShowMoreModal}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 3,
                borderRadius: 2,
                maxWidth: "480px",
                width: "90%",
                maxHeight: "70vh",
                overflowY: "auto",
              }}
            >
              <IconButton
                onClick={closeShowMoreModal}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, pr: 4 }}>
                {showMoreDate
                  ? moment(showMoreDate).format("dddd, MMM D, YYYY")
                  : "Events"}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {showMoreEvents.map((event) => (
                  <Button
                    key={`${event.ticketid}-${event.start.toISOString()}`}
                    variant="outlined"
                    onClick={() => handleShowMoreEventClick(event)}
                    sx={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      textTransform: "none",
                      py: 1.5,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {event.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {event.allDay
                          ? "All day"
                          : `${moment(event.start).format("h:mm A")} – ${moment(event.end).format("h:mm A")}`}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Box>
            </Box>
          </Modal>

          <Modal open={!!selectedEvent} onClose={closeModal}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
                maxWidth: "500px",
                width: "90%",
              }}
            >
              {selectedEvent && (
                <>
                  <IconButton
                    onClick={closeModal}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      fontSize: "1rem",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    Ticket Details
                  </Typography>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      marginTop: "16px",
                    }}
                  >
                    {getTicketDetails(selectedEvent).map((item) => (
                      <div
                        key={item.label}
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                            flex: 1,
                            textAlign: "right",
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.875rem", margin: "0 8px" }}
                        >
                          :
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.875rem",
                            flex: 2,
                            textAlign: "left",
                          }}
                        >
                          {item.value}
                        </Typography>
                      </div>
                    ))}
                  </div>

                  {mapQuery && (
                    <Link
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "block",
                        textAlign: "center",
                        marginTop: "16px",
                        color: "primary.main",
                        textDecoration: "none",
                        fontSize: "14px",
                      }}
                    >
                      View on Google Maps
                    </Link>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    onClick={handleViewTicket}
                    sx={{
                      mt: 3,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    View Ticket
                  </Button>
                </>
              )}
            </Box>
          </Modal>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EventsCalendar;
